const FENCE_OPEN_PATTERN = /^ {0,3}(`{3,}|~{3,})/;
const FENCE_CLOSE_PATTERN = /^ {0,3}(`{3,}|~{3,})\s*$/;
const LIST_ITEM_PATTERN = /^\s*(?:[-*+]|\d+[.)])\s/;
const ATX_HEADING_PATTERN = /^ {0,3}#{1,6}(?:\s|$)/;
const THEMATIC_BREAK_PATTERN = /^ {0,3}(?:(?:-\s*){3,}|(?:\*\s*){3,}|(?:_\s*){3,})$/;
const LEADING_WHITESPACE_PATTERN = /^[ \t]*/;
const TAB_WIDTH = 4;

interface Fence {
  char: string;
  length: number;
}

function openingFence(line: string): Fence | null {
  const match = FENCE_OPEN_PATTERN.exec(line);
  return match ? { char: match[1][0], length: match[1].length } : null;
}

// CommonMark closes a fence only with the same character, at least as many of
// them as the opener, and nothing but whitespace after.
function closesFence(line: string, fence: Fence): boolean {
  const match = FENCE_CLOSE_PATTERN.exec(line);
  return match !== null && match[1][0] === fence.char && match[1].length >= fence.length;
}

// Indented code cannot interrupt a paragraph, but it can start right after any
// line that a paragraph cannot lazily continue through.
function endsParagraph(line: string): boolean {
  return ATX_HEADING_PATTERN.test(line) || THEMATIC_BREAK_PATTERN.test(line);
}

function indentColumns(line: string): number {
  let columns = 0;
  for (const char of line) {
    if (char === " ") columns += 1;
    else if (char === "\t") columns += TAB_WIDTH - (columns % TAB_WIDTH);
    else break;
  }
  return columns;
}

/**
 * LLM answers sometimes indent prose (an address block, a quoted clause) by
 * four or more spaces. CommonMark renders that as an indented code block, so it
 * shows in monospace and any markdown inside it (page links, citation markers)
 * is emitted as literal text. Strip the indentation from those blocks while
 * leaving fenced code and list-nested content alone.
 */
export function dedentIndentedCodeBlocks(markdown: string): string {
  const lines = markdown.split("\n");
  const output: string[] = [];

  let openFence: Fence | null = null;
  let inList = false;
  let inIndentedBlock = false;
  let blockMayStart = true;

  for (const line of lines) {
    if (openFence) {
      output.push(line);
      if (closesFence(line, openFence)) {
        openFence = null;
        blockMayStart = true;
      }
      continue;
    }

    const fence = openingFence(line);
    if (fence) {
      openFence = fence;
      inIndentedBlock = false;
      inList = false;
      blockMayStart = false;
      output.push(line);
      continue;
    }

    if (line.trim() === "") {
      output.push(line);
      blockMayStart = true;
      continue;
    }

    const columns = indentColumns(line);
    const indented = columns >= TAB_WIDTH;

    if (inIndentedBlock && indented) {
      output.push(line.replace(LEADING_WHITESPACE_PATTERN, ""));
      blockMayStart = false;
      continue;
    }
    inIndentedBlock = false;

    if (indented && blockMayStart && !inList) {
      inIndentedBlock = true;
      output.push(line.replace(LEADING_WHITESPACE_PATTERN, ""));
      blockMayStart = false;
      continue;
    }

    if (LIST_ITEM_PATTERN.test(line)) {
      inList = true;
    } else if (columns === 0) {
      inList = false;
    }

    output.push(line);
    blockMayStart = endsParagraph(line);
  }

  return output.join("\n");
}
